import React, { useState, useEffect } from 'react';
import ReactMde from 'react-mde';
import Showdown from 'showdown';
import 'react-mde/lib/styles/css/react-mde-all.css';
import 'github-markdown-css';
import { doc, getDoc, setDoc } from "firebase/firestore";
import { db } from "../firebase";

// Configure the Showdown converter to allow raw HTML (e.g., your GIF)
const converter = new Showdown.Converter({
  tables: true,
  simplifiedAutoLink: true,
  strikethrough: true,
  tasklists: true,
  simpleLineBreaks: true,
  sanitize: false, // Ensure raw HTML is not stripped
});

const HeroSection = ({ darkMode, profileImage, isAdmin }) => {
  // State to store hero content; initial value is "README.md"
  const [heroContent, setHeroContent] = useState("README.md");
  // Track whether we are in edit mode
  const [isEditing, setIsEditing] = useState(false);
  // Control the selected tab for the Markdown editor
  const [selectedTab, setSelectedTab] = useState('write');

  // Load the hero content from Firestore on mount
  useEffect(() => {
    const fetchHeroContent = async () => {
      try {
        const heroDocRef = doc(db, "site-settings", "hero");
        const heroDoc = await getDoc(heroDocRef);
        if (heroDoc.exists()) {
          setHeroContent(heroDoc.data().content);
        }
      } catch (error) {
        console.error("Error fetching hero content:", error);
      }
    };

    fetchHeroContent();
  }, []);

  // Save the hero content to Firestore
  const handleSave = async () => {
    try {
      await setDoc(
        doc(db, "site-settings", "hero"),
        { content: heroContent },
        { merge: true }
      );
      setIsEditing(false);
    } catch (error) {
      console.error("Error saving hero section:", error);
      alert("Error saving hero section. Please try again.");
    }
  };

  return (
    <section
      className={`hero-section rounded-3xl overflow-hidden mb-16 relative fade-in-up ${
        darkMode ? 'bg-gradient-to-br from-gray-800 via-gray-900 to-gray-800' : 'bg-gradient-to-br from-indigo-50 via-purple-50 to-pink-50'
      }`}
    >
      {/* Animated background effect */}
      <div className="absolute inset-0 opacity-30">
        <div className={`absolute top-0 left-0 w-72 h-72 rounded-full filter blur-3xl ${darkMode ? 'bg-purple-600' : 'bg-indigo-300'} animate-pulse`}></div>
        <div className={`absolute bottom-0 right-0 w-96 h-96 rounded-full filter blur-3xl ${darkMode ? 'bg-indigo-600' : 'bg-purple-300'} animate-pulse`} style={{animationDelay: '2s'}}></div>
      </div>

      <div className="container mx-auto relative z-10">
        <div className="flex flex-col md:flex-row items-center">
          {/* Profile Image Side */}
          <div className="w-full md:w-1/3 flex justify-center p-6">
            <div className="relative hover-scale">
              {/* Animated ring around profile */}
              <div className={`absolute inset-0 rounded-full ${darkMode ? 'bg-gradient-to-r from-purple-500 via-indigo-500 to-purple-500' : 'bg-gradient-to-r from-indigo-400 via-purple-400 to-pink-400'} opacity-75 blur-lg animate-pulse`}></div>
              <div
                className={`relative w-64 h-64 rounded-full overflow-hidden border-4 shadow-2xl ${
                  darkMode ? 'border-purple-500/50 shadow-purple-500/20' : 'border-white shadow-indigo-300/40'
                }`}
              >
                <img
                  src={profileImage || "/profile-placeholder.jpg"}
                  alt="Profile"
                  className="w-full h-full object-cover transition-transform duration-500 hover:scale-110"
                />
              </div>
            </div>
          </div>

          {/* Info Side */}
          <div className="w-full md:w-2/3 p-6 md:p-10">
            {/* Edit button appears only if admin and not editing */}
            {isAdmin && !isEditing && (
              <div className="flex justify-end mb-4">
                <button
                  onClick={() => setIsEditing(true)}
                  className={`px-5 py-2.5 rounded-lg font-medium transition-all duration-300 hover:scale-105 hover-lift ${
                    darkMode ? 'bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 text-white shadow-lg shadow-purple-500/30' : 'bg-gradient-to-r from-indigo-500 to-purple-500 hover:from-indigo-600 hover:to-purple-600 text-white shadow-lg shadow-indigo-300/40'
                  }`}
                >
                  Edit
                </button>
              </div>
            )}

            {isEditing ? (
              <div className="mb-6">
                <ReactMde
                  value={heroContent}
                  onChange={setHeroContent}
                  selectedTab={selectedTab}
                  onTabChange={setSelectedTab}
                  generateMarkdownPreview={(markdown) =>
                    Promise.resolve(markdown)
                  }
                />
                <div className="mt-4 flex space-x-4">
                  <button
                    onClick={handleSave}
                    className={`px-5 py-2.5 rounded-lg font-medium transition-all duration-300 hover:scale-105 ${
                      darkMode
                        ? 'bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 text-white shadow-lg shadow-purple-500/30'
                        : 'bg-gradient-to-r from-indigo-500 to-purple-500 hover:from-indigo-600 hover:to-purple-600 text-white shadow-lg shadow-indigo-300/40'
                    }`}
                  >
                    Save
                  </button>
                  <button
                    onClick={() => setIsEditing(false)}
                    className={`px-5 py-2.5 rounded-lg font-medium transition-all duration-300 hover:scale-105 ${
                      darkMode
                        ? 'bg-gray-700 hover:bg-gray-600 text-white shadow-md'
                        : 'bg-gray-200 hover:bg-gray-300 text-gray-800 shadow-md'
                    }`}
                  >
                    Cancel
                  </button>
                </div>
              </div>
            ) : (
              <div
                className={`markdown-body mb-6 fade-in ${darkMode ? 'prose-invert' : ''}`}
                style={{
                  backgroundColor: 'transparent',
                  border: 'none',
                  padding: 0,
                  color: darkMode ? 'white' : 'black',
                }}
                dangerouslySetInnerHTML={{
                  __html: converter.makeHtml(heroContent),
                }}
              />
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

export default HeroSection;
